<script setup>
import { ref, computed, watch } from 'vue'

const { $store, $filters, $eventBus } = useNuxtApp()
const props = defineProps({
  head: Object,
  sortBy: String,
  sortDesc: Boolean,
  isActive: Boolean
})
const emits = defineEmits(['update:apply'])
const type = ref()
const options = ref()
const operator = ref('')
const value = ref()
const disallowedOperators = ['IN', 'NOT_IN']
const sortDirective = ref({
  sortBy: '',
  sortDesc: false
})

// COMPUTED

const ruleOptions = computed(() => [
  { text: 'Select Rule', value: '' },
  ...(options.value?.eligibleOperators || [])
    .filter(op => !disallowedOperators.includes(op))
    .map(op => ({
      text: $filters.convertFromCapsUnderscore(op),
      value: op
    }))
])

const filterSet = computed(() =>
  $store.getters['bulkEdit/filterSets']
    ?.find(fs =>
      fs.entityType === type.value &&
      fs.filters.length === 1 &&
      fs.filters[0].fieldName === props.head.column &&
      !disallowedOperators.includes(fs.filters[0].operator)
    )
)

const active = computed(() => props.isActive || !!filterSet.value || (sortDirective.value.sortBy && sortDirective.value.sortBy === props.head.column))
const approvedFilters = computed(() => $store.getters['bulkEdit/approvedBulkFilters'] || {})

// WATCHERS

watch(
  () => filterSet.value?.filters?.[0]?.operator,
  (newValue) => {
    if (newValue) {
      operator.value = newValue
    } else {
      operator.value = ''
    }
  },
  { immediate: true }
)

watch(
  () => filterSet.value?.filters?.[0]?.values?.[0],
  (newValue) => {
    if (newValue) {
      value.value = props.head.column.includes('(%)') ? newValue * 100 : newValue
    } else {
      value.value = ''
    }
  },
  { immediate: true }
)

watch(
  () => props.sortBy,
  (value) => {
    sortDirective.value.sortBy = value
  },
  { immediate: true }
)

watch(
  () => props.sortDesc,
  (value) => {
    sortDirective.value.sortDesc = value
  },
  { immediate: true }
)

// METHODS

const findFilter = () => {
  if (!type.value || !options.value) {
    for (const [cg, opts] of Object.entries(approvedFilters.value)) {
      const o = opts.find(opt => opt.fieldName === props.head.column)
      if (o) {
        type.value = cg
        options.value = o
      }
    }
  }
}

watch(
  () => approvedFilters.value,
  findFilter,
  { immediate: true }
)

const apply = async () => {
  emits('update:apply', sortDirective.value)
  if ((value.value && operator.value) || isBooleanOption(operator.value)) {
    let v = value.value
    if (props.head.column.includes('(%)')) {
      v = v / 100
    }
    if (filterSet.value) {
      await $store.commit('bulkEdit/updateFilter', {
        filterSet: filterSet.value,
        filter: filterSet.value.filters[0],
        changes: {
          operator: operator.value,
          values: [v]
        }
      })
    } else {
      await $store.commit('bulkEdit/addCompleteFilterSet', {
        entityType: type,
        filterStrategy: 'ALL_MATCH',
        filters: [
          {
            fieldName: props.head.column,
            operator: operator.value,
            values: [v]
          }
        ]
      })
    }
  }
  if ((value.value && operator.value) || isBooleanOption(operator.value) || sortDirective.value.sortBy === props.head.column) {
    $eventBus.$emit('fetchDXData', { ...sortDirective.value })
  }
}

const updateSort = (desc) => {
  sortDirective.value.sortBy = props.head.column
  sortDirective.value.sortDesc = desc
}

const isBooleanOption = (field) => {
  return ['IS_BLANK', 'IS_NOT_BLANK', 'IS_TRUE', 'IS_FALSE', 'IS_NOT_TRUE', 'IS_NOT_FALSE'].includes(field)
}

</script>

<template>
  <div class="table-column-filter no-print" @click.stop>
    <t-dropdown variant="link" class="pl-2" style="top: 1px" :toggle-class="`p-0 border-0 overflow-visible-important ${active ? 'text-info' : 'text-secondary'}`" lazy no-caret>
      <template #button-content>
        <fluency-icon type="filter" class="table-filter-icon" />
      </template>
      <template #default="{ hide }">
        <div class="d-flex-center" style="min-width: 280px;">
          <h4 class="mb-0">{{ props.head.label }}</h4>
          <t-button-inline severity="secondary" class="ml-auto" @click="hide()">
            <fluency-icon type="close" />
          </t-button-inline>
        </div>
        <div v-if="ruleOptions.length > 1" class="d-flex mt-4">
          <b-form-group label="Rule" label-class="font-weight-normal small">
            <b-form-select v-model="operator" :options="ruleOptions" />
          </b-form-group>
          <b-form-group v-if="!isBooleanOption(operator)" class="ml-2" label="Value" label-class="font-weight-normal small">
            <b-form-input v-model="value" placeholder="Enter Value" />
          </b-form-group>
        </div>
        <slot name="filter-body"></slot>
        <div class="my-3 d-flex">
          <b-form-radio class="mr-3 font-weight-normal" :checked="sortDirective.sortBy === props.head.column && sortDirective.sortDesc === false" :value="true" @change="updateSort(false)">Ascending</b-form-radio>
          <b-form-radio class="font-weight-normal" :checked="sortDirective.sortBy === props.head.column && sortDirective.sortDesc === true" :value="true" @change="updateSort(true)">Descending</b-form-radio>
        </div>
        <div class="d-flex justify-content-end">
          <t-button variant="primary" @click="apply(), hide()">Apply</t-button>
        </div>
      </template>
    </t-dropdown>
  </div>
</template>

<style lang="scss" scoped>
.table-filter-icon {
  position: relative;
  display: inline-block;
  font-size: 78%;
  width: 10px;
  transform: translate(-1px, 0);
}
</style>
